import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts'

const COLORS = ['#2563eb', '#0ea5e9', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444']

export default function Dashboard() {
  const [range, setRange] = useState('month')
  const [data, setData] = useState(null)

  useEffect(() => {
    api.get(`/analytics/overview?range=${range}`).then((res) => setData(res.data.data)).catch(() => setData(null))
  }, [range])

  const totals = data?.totals || {}

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Analytics</h1>
        <select value={range} onChange={(e) => setRange(e.target.value)} className="admin-input w-full sm:w-auto">
          <option value="day">Today</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
          <option value="year">Year</option>
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          ['Live now', totals.liveVisitors],
          ['Total visitors', totals.totalVisitors],
          ['Page views', totals.totalPageViews],
          ['Today', totals.visitorsToday],
          ['This week', totals.visitorsWeek],
          ['This month', totals.visitorsMonth],
          ['This year', totals.visitorsYear],
          ['Unread messages', data?.content?.unreadMessages],
        ].map(([label, value]) => (
          <div key={label} className="admin-glass rounded-2xl p-4">
            <p className="text-slate-400 text-sm">{label}</p>
            <p className="text-2xl font-bold mt-1">{value ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="admin-glass rounded-2xl p-4 h-80">
          <h3 className="mb-4 font-semibold">Visitor trend</h3>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" />
              <Tooltip />
              <Line type="monotone" dataKey="visitors" stroke="#38bdf8" />
              <Line type="monotone" dataKey="pageViews" stroke="#6366f1" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="admin-glass rounded-2xl p-4 h-80">
          <h3 className="mb-4 font-semibold">Top pages</h3>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.topPages || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="path" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" />
              <Tooltip />
              <Bar dataKey="views" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {[
          ['Devices', data?.devices],
          ['Browsers', data?.browsers],
          ['Traffic sources', data?.sources],
        ].map(([title, rows]) => (
          <div key={title} className="admin-glass rounded-2xl p-4 h-72">
            <h3 className="mb-4 font-semibold">{title}</h3>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows || []} dataKey="value" nameKey="name" outerRadius={80} label>
                  {(rows || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>

      <div className="mt-8 admin-glass rounded-2xl p-4 overflow-x-auto">
        <h3 className="mb-4 font-semibold">Countries</h3>
        <table className="w-full text-sm">
          <thead className="text-slate-400">
            <tr><th className="text-left py-2">Country</th><th>Visitors</th><th>Views</th></tr>
          </thead>
          <tbody>
            {(data?.countries || []).map((row) => (
              <tr key={row.name} className="border-t border-slate-800">
                <td className="py-2">{row.name}</td>
                <td className="text-center">{row.visitors}</td>
                <td className="text-center">{row.views}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
